import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { contactAPI, partnershipAPI, ordersAPI, ecuTuningAPI } from '../services/api';

const Navbar = () => {
  const { pathname } = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { cart } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [newContactCount, setNewContactCount] = useState(0);
  const [newPartnerCount, setNewPartnerCount] = useState(0);
  const [newEcuCount, setNewEcuCount] = useState(0);
  const langMenuRef = useRef(null);

  // Fetch notification counts for admin users
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      if (user?.role === 'admin') {
        try {
          const [ordersRes, contactRes, partnershipRes, ecuRes] = await Promise.all([
            ordersAPI.getAllAdmin({ status: 'pending' }).catch(() => ({ data: [] })),
            contactAPI.getAll({ status: 'new' }).catch(() => ({ data: { total: 0 } })),
            partnershipAPI.getAll({ status: 'new' }).catch(() => ({ data: { total: 0 } })),
            ecuTuningAPI.getAll({ status: 'new' }).catch(() => ({ data: { total: 0 } }))
          ]);
          
          // Count pending orders
          const pendingOrders = Array.isArray(ordersRes.data) 
            ? ordersRes.data.filter(order => order.status === 'pending').length 
            : 0;
          
          setNewOrdersCount(pendingOrders);
          setNewContactCount(contactRes.data.total || 0);
          setNewPartnerCount(partnershipRes.data.total || 0);
          setNewEcuCount(ecuRes.data.total || 0);
        } catch (error) {
          // Silently fail for background polling
        }
      }
    };

    // Only run if user is loaded and is admin
    if (user?.role === 'admin') {
      fetchNotificationCounts();
      // Refresh every 30 seconds
      const interval = setInterval(fetchNotificationCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  ];

  const currentLang = languages.find(lang => lang.code === language);

  return (
    <nav className={`bg-racing-black text-white shadow-lg sticky top-0 z-50${pathname === '/' ? ' home-navbar' : ''}`}>
      <div className="container-custom">
        <div className="flex items-center justify-between gap-4 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="/logo-dark.png" 
              alt="Meller Racing" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-6 xl:gap-8">
            <Link to="/" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.home')}
            </Link>
            <Link to="/tuning" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.tuning')}
            </Link>
            <Link to="/workshop" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.workshop')}
            </Link>
            <Link to="/shop" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.shop')}
            </Link>
            <Link to="/tuning-points" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.tuningPoints')}
            </Link>
            <Link to="/contact" className="hover:text-racing-red transition whitespace-nowrap text-sm xl:text-base">
              {t('nav.contact')}
            </Link>
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6 flex-shrink-0">
            {/* Language Switcher */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-racing-red hover:bg-red-700 transition font-semibold shadow-lg text-sm"
              >
                <span className="text-lg">{currentLang?.flag}</span>
                <span className="text-sm font-bold">{currentLang?.code.toUpperCase()}</span>
                <svg className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white text-gray-900 rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
                  {languages.map((lang, index) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center space-x-3 transition-colors ${
                        language === lang.code ? 'bg-racing-red text-white font-semibold hover:bg-red-700' : ''
                      } ${index !== languages.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                      {language === lang.code && (
                        <span className="ml-auto">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Cart Icon - Always Visible */}
            <Link to="/cart" className="relative hover:text-racing-red transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {cart.item_count > 0 && (
                <span className="absolute -top-2 -right-2 bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cart.item_count}
                </span>
              )}
            </Link>
            
            {isAuthenticated() ? (
              <>
                <div className="relative group">
                  <button className="flex items-center space-x-2 hover:text-racing-red transition relative min-w-0 max-w-[180px]">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="truncate">{user?.username}</span>
                    {user?.role === 'admin' && (newOrdersCount + newContactCount + newPartnerCount + newEcuCount) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-racing-red text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1">
                        {newOrdersCount + newContactCount + newPartnerCount + newEcuCount}
                      </span>
                    )}
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white text-gray-900 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link to="/profile" className="block px-4 py-2 hover:bg-gray-100 rounded-t-lg">
                      {t('nav.profile')}
                    </Link>
                    {user?.role === 'admin' ? (
                      <>
                        <Link to="/admin/orders" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red relative">
                          <span className="flex items-center justify-between">
                            <span>{t('nav.adminOrders') || '👑 Manage Orders'}</span>
                            {newOrdersCount > 0 && (
                              <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ml-2">
                                {newOrdersCount}
                              </span>
                            )}
                          </span>
                        </Link>
                        <Link to="/admin/contact-messages" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red relative">
                          <span className="flex items-center justify-between">
                            <span>{t('nav.adminMessages') || '💬 Contact Messages'}</span>
                            {newContactCount > 0 && (
                              <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ml-2">
                                {newContactCount}
                              </span>
                            )}
                          </span>
                        </Link>
                        <Link to="/admin/partnership-requests" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red relative">
                          <span className="flex items-center justify-between">
                            <span>{t('nav.adminPartners') || '🤝 Partner Messages'}</span>
                            {newPartnerCount > 0 && (
                              <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ml-2">
                                {newPartnerCount}
                              </span>
                            )}
                          </span>
                        </Link>
                        <Link to="/admin/newsletter" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red">
                          <span>{t('nav.adminNewsletter') || '📧 Newsletter Management'}</span>
                        </Link>
                        <Link to="/admin/instagram" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red">
                          <span>{t('nav.adminInstagram') || '📸 Instagram Posts'}</span>
                        </Link>
                        <Link to="/admin/ecu-tuning" className="block px-4 py-2 hover:bg-gray-100 bg-yellow-50 font-semibold text-racing-red relative">
                          <span className="flex items-center justify-between">
                            <span>{t('nav.adminEcuTuning') || '⚙️ ECU Tuning Requests'}</span>
                            {newEcuCount > 0 && (
                              <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ml-2">
                                {newEcuCount}
                              </span>
                            )}
                          </span>
                        </Link>
                      </>
                    ) : (
                      <Link to="/orders" className="block px-4 py-2 hover:bg-gray-100">
                        {t('nav.orders')}
                      </Link>
                    )}
                    <button onClick={logout} className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-b-lg text-racing-red">
                      {t('nav.logout')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="hover:text-racing-red transition">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="btn btn-primary">
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu — fixed full-screen overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-0 z-40 bg-racing-black flex flex-col">
            {/* Header row with logo + close button */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
              <img src="/logo-dark.png" alt="Meller Racing" className="h-10 w-auto" />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 transition text-white border border-gray-600"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto py-4 px-4">
            <div className="flex flex-col space-y-4">
              {/* Mobile Language Switcher */}
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 px-2 uppercase tracking-wider">Language</div>
                <div className="flex flex-wrap gap-2 px-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
                        language === lang.code
                          ? 'bg-racing-red text-white'
                          : 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-4"></div>
              
              <Link to="/" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.home')}
              </Link>
              <Link to="/tuning" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.tuning')}
              </Link>
              <Link to="/workshop" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.workshop')}
              </Link>
              <Link to="/shop" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.shop')}
              </Link>
              <Link to="/tuning-points" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.tuningPoints')}
              </Link>
              <Link to="/contact" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.contact')}
              </Link>
              
              {/* Cart - Always Visible */}
              <Link to="/cart" className="hover:text-racing-red transition flex items-center justify-between" onClick={() => setIsMobileMenuOpen(false)}>
                <span>{t('nav.cart')}</span>
                {cart.item_count > 0 && (
                  <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                    {cart.item_count}
                  </span>
                )}
              </Link>
              
              {isAuthenticated() ? (
                <>
                  <Link to="/profile" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.profile')}
                  </Link>
                  {user?.role === 'admin' ? (
                    <>
                      <Link to="/admin/orders" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg flex items-center justify-between" onClick={() => setIsMobileMenuOpen(false)}>
                        <span>👑 {t('nav.adminOrders') || 'Manage Orders'}</span>
                        {newOrdersCount > 0 && (
                          <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {newOrdersCount}
                          </span>
                        )}
                      </Link>
                      <Link to="/admin/contact-messages" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg flex items-center justify-between" onClick={() => setIsMobileMenuOpen(false)}>
                        <span>💬 {t('nav.adminMessages') || 'Contact Messages'}</span>
                        {newContactCount > 0 && (
                          <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {newContactCount}
                          </span>
                        )}
                      </Link>
                      <Link to="/admin/partnership-requests" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg flex items-center justify-between" onClick={() => setIsMobileMenuOpen(false)}>
                        <span>🤝 {t('nav.adminPartners') || 'Partner Messages'}</span>
                        {newPartnerCount > 0 && (
                          <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {newPartnerCount}
                          </span>
                        )}
                      </Link>
                      <Link to="/admin/ecu-tuning" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg flex items-center justify-between" onClick={() => setIsMobileMenuOpen(false)}>
                        <span>⚙️ {t('nav.adminEcuTuning') || 'ECU Tuning Requests'}</span>
                        {newEcuCount > 0 && (
                          <span className="bg-racing-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {newEcuCount}
                          </span>
                        )}
                      </Link>
                      <Link to="/admin/newsletter" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>
                        📧 {t('nav.adminNewsletter') || 'Newsletter Management'}
                      </Link>
                      <Link to="/admin/instagram" className="bg-yellow-50 text-racing-red font-semibold px-4 py-2 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>
                        📸 {t('nav.adminInstagram') || 'Instagram Posts'}
                      </Link>
                    </>
                  ) : (
                    <Link to="/orders" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                      {t('nav.orders')}
                    </Link>
                  )}
                  <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="text-left hover:text-racing-red transition">
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="hover:text-racing-red transition" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.login')}
                  </Link>
                  <Link to="/register" className="btn btn-primary" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.register')}
                  </Link>
                </>
              )}
            </div>
            </div>{/* end scrollable */}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
